import type { AgendaItem, StageMode } from "@msc/shared";

/**
 * Zwei reine Entscheidungen rund um die Agenda-Punkte (D9), damit sie ohne
 * React prüfbar bleiben: Wann übernimmt die Vorbereitung die Vorlagen aus der
 * API? Und was kommt als Nächstes?
 */

/**
 * Darf die Vorbereitung die frisch geladenen Vorlagen in den Store schreiben?
 *
 * Nur zwei Fälle sagen ja: Der Store ist leer (erster Termin), oder seine
 * Punkte gehören zu einem ANDEREN Kunden. Der zweite Fall ist der eigentliche
 * Grund für `agendaObjectKey`: Ohne ihn nähme der Lead beim Kundenwechsel die
 * Wartungstabelle des vorigen Termins mit — sie sähe plausibel aus, und die
 * Liste verriete die Herkunft mit keinem Zeichen.
 *
 * Bereits übernommene Punkte werden NIE stillschweigend überschrieben; dafür
 * gibt es den ausdrücklichen Knopf „Vorlagen neu laden".
 */
export function shouldAdoptAgenda(o: {
  storeItems: AgendaItem[];
  storeObjectKey: string | null;
  objectKey: string;
}): boolean {
  return o.storeItems.length === 0 || o.storeObjectKey !== o.objectKey;
}

/** Was nach der aktuellen Folie kommt — Agenda-Punkt oder Ticket. */
export type NextUp =
  | { kind: "agendaItem"; index: number; title: string }
  | { kind: "ticket"; index: number; key: string }
  | null;

/**
 * Die „Als Nächstes"-Vorschau des Cockpits. Sie muss dieselben Übergänge
 * kennen wie `next()` im Store — sonst kündigt sie etwas an, das der nächste
 * Klick nicht zeigt. Deshalb steht die Logik hier einmal als reine Funktion
 * und nicht als zweite Ableitung im Bauteil.
 *
 * `agendaIndex` zählt über die AKTIVIERTEN Punkte (dieselbe Zählung wie im
 * Snapshot und im Store). Abgewählte Punkte kommen nicht vor.
 */
export function nextUp(o: {
  mode: StageMode;
  agendaItems: AgendaItem[];
  agendaIndex: number;
  deck: string[];
  currentIndex: number;
}): NextUp {
  const enabled = o.agendaItems.filter((a) => a.enabled);
  /** Hinter den Agenda-Punkten steht das erste Ticket — oder nichts mehr. */
  const erstesTicket = (): NextUp => {
    const key = o.deck[0];
    return key === undefined ? null : { kind: "ticket", index: 0, key };
  };
  const agendaAt = (i: number): NextUp => {
    const item = enabled[i];
    return item === undefined ? null : { kind: "agendaItem", index: i, title: item.title };
  };

  if (o.mode === "agenda") return agendaAt(0) ?? erstesTicket();
  if (o.mode === "agendaItem") return agendaAt(o.agendaIndex + 1) ?? erstesTicket();
  if (o.mode === "ticket") {
    const key = o.deck[o.currentIndex + 1];
    return key === undefined ? null : { kind: "ticket", index: o.currentIndex + 1, key };
  }
  // Pause und Ende kündigen nichts an: In der Pause steht der Termin, am Ende
  // ist er vorbei.
  return null;
}
